import {hpcAttrs} from '@github-ui/hpc'
import {SoftNavOnlyLink as RouterLink, useLocation, useNavigation, useSearchParams} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {Pagination} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {useQueryClient} from '@github-ui/react-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {FilterSelectType} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {SharedListLoadingSkeleton} from '@github-ui/shared-dashboard-lists/shared/SharedListLoadingSkeleton'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {useSso} from '@github-ui/use-sso'
import {useCallback, useMemo} from 'react'

import {type DashboardListContainerProps, useDashboardIssueSidePanel} from '../hooks/use-dashboard-issue-side-panel'
import {useEnrichedIssues} from '../hooks/use-enriched-issues'
import type {SettleTarget} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {rowNodeKey, rowNodeSettleTargets} from '../hooks/issues-dashboard-row-node'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {
  useDashboardIssueMutationBridge,
  useOptimisticDashboardRows,
} from '../hooks/use-dashboard-issue-optimistic-updates'
import {useIssuesDashboardState} from '../hooks/use-issues-dashboard-state'
import {issuesDashboardSurfaceContentRoute} from './issues-dashboard-surface-content-route'
import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {useIssuesDashboardSurfaceStateContext} from '../contexts/IssuesDashboardSurfaceStateContext'
import {useIssuesDashboardSurfaceLayoutState} from '../hooks/use-issues-dashboard-surface-layout-state'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {DEFAULT_SORTING_KEY} from '@github-ui/shared-dashboard-surface/constants/pulls-sorting'
import {
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '@github-ui/shared-dashboard-lists/shared/consts/sorting-consts'
import {ISSUES_DASHBOARD_SEARCH_PATH} from '../constants/routes'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {isExplicitIssueQuery} from '../helpers/is-explicit-issue-query'
import {ssoOrgsInQuery} from '../helpers/sso-orgs-in-query'
import {addIssueFieldToQuery, createReplaceIssueFieldQuery} from '@github-ui/issue-url-helper'
import {
  addRepoFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
  addAssigneeFilterToQuery,
  addTypeFilterToQuery,
  addBlockedFilterToQuery,
  addParentIssueFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

export function IssuesDashboardSurfaceContentComponent() {
  const navigation = useNavigation()
  const location = useLocation()

  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.has('q')
  const isOnSearchPageWithNoQuery = location.pathname === ISSUES_DASHBOARD_SEARCH_PATH && !searchQuery

  // Read the list non-suspensefully: `useEnrichedIssues` suspends, and suspending here would tear
  // the panel down on every refresh. j/k only needs row identity, so the unenriched rows suffice.
  const {data: listData} = useRouteQuery(issuesDashboardSurfaceContentRoute, 'mainQuery')

  // Navigable issues in list order for j/k keyboard navigation while the panel is open. PRs
  // can't open in the issue side panel, so excluding them here makes "next/previous issue"
  // naturally skip over any PR rows in the mixed list.
  const navigableIssues = useMemo<DashboardIssueIdentifier[]>(
    () =>
      (listData?.results ?? []).flatMap(item => {
        if (item.itemType !== 'issue') return []
        // Rows carry the repository as a joined `"owner/repo"`; the issue viewer needs the parts separately.
        const [owner = '', repo = ''] = item.repoNameWithOwner.split('/')
        return [{owner, repo, number: item.number, globalId: item.globalId}]
      }),
    [listData?.results],
  )

  const queryClient = useQueryClient()
  const mainQueryKey = useRouteQueryKey(issuesDashboardSurfaceContentRoute, 'mainQuery')
  const deferredQueryKey = useRouteQueryKey(issuesDashboardSurfaceContentRoute, 'deferred')

  const refreshList = useCallback(() => {
    // Invalidate rather than re-navigate. A panel edit or delete leaves the query and page
    // unchanged, so `fetchResults` would navigate to the URL the user is already on and refetch
    // nothing. Invalidating is also cheaper: no history entry, no suspense, no skeleton swap.
    for (const queryKey of [mainQueryKey, deferredQueryKey]) {
      if (queryKey) queryClient.invalidateQueries({queryKey})
    }
  }, [queryClient, mainQueryKey, deferredQueryKey])

  // Owned here rather than in `IssuesDashboardPageContent`: that child is unmounted both by the
  // `isNavigating` skeleton swap and by `useEnrichedIssues`' suspense, so panel state kept there
  // would be lost on every refresh. This route component survives a same-route refresh.
  const {onIssueSelect, isSidePanelOpen, sidePanelIssue, listContainerProps, sidePanel} = useDashboardIssueSidePanel({
    navigableIssues,
    refreshList,
  })

  const activeRow = useMemo(
    () =>
      sidePanelIssue
        ? listData?.results.find(
            row =>
              row.itemType === 'issue' &&
              ((sidePanelIssue.globalId && row.globalId === sidePanelIssue.globalId) ||
                (row.number === sidePanelIssue.number &&
                  row.repoNameWithOwner === `${sidePanelIssue.owner}/${sidePanelIssue.repo}`)),
          )
        : undefined,
    [listData?.results, sidePanelIssue],
  )

  const activeOwnerId = activeRow?.globalId ?? sidePanelIssue?.globalId
  // Two kinds of cache hold these rows: the list query, and a per-row node cache for every row with
  // a live-update channel. Both have to receive a confirmed operation — see `rowNodeSettleTargets`
  // for why registering only the panel's row is not enough. `settleConfirmedOperation` filters
  // targets by whether they actually contain the entity, so unrelated rows cost a cache lookup.
  const settleTargets = useMemo(
    () => [
      // `refetchType: 'all'` because the list query can be inactive while the side panel is open;
      // an 'active'-only invalidate would leave it stale until something else refetched it.
      ...(mainQueryKey ? [{queryKey: mainQueryKey, refetchType: 'all' as const}] : []),
      ...rowNodeSettleTargets(
        listData?.results ?? [],
        activeOwnerId ?? (activeRow ? rowNodeKey(activeRow) : undefined),
      ),
    ],
    [mainQueryKey, listData?.results, activeOwnerId, activeRow],
  )
  useDashboardIssueMutationBridge({ownerId: activeOwnerId})

  return (
    <>
      {isNavigating ? (
        <SharedListLoadingSkeleton dashboardType="issues" numItems={5} />
      ) : (
        <ErrorBoundary
          boundaryName="IssuesDashboardContent"
          fallback={
            <div className="tmp-my-3">
              <SharedDashboardBlankslate dashboardType="issues" variant="error" />
            </div>
          }
        >
          {isOnSearchPageWithNoQuery ? (
            <SharedDashboardBlankslate dashboardType="issues" variant="emptySearch" />
          ) : (
            <IssuesDashboardPageContent
              onIssueSelect={onIssueSelect}
              isSidePanelOpen={isSidePanelOpen}
              listContainerProps={listContainerProps}
              settleTargets={settleTargets}
            />
          )}
        </ErrorBoundary>
      )}
      {sidePanel}
    </>
  )
}

function IssuesDashboardPageContent({
  onIssueSelect,
  isSidePanelOpen,
  listContainerProps,
  settleTargets,
}: {
  onIssueSelect: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  isSidePanelOpen: boolean
  listContainerProps: DashboardListContainerProps
  settleTargets: SettleTarget[]
}) {
  const location = useLocation()
  const {issues: serverIssues, pageInfo, isPending, error} = useEnrichedIssues()
  const issues = useOptimisticDashboardRows({
    base: serverIssues,
    targets: settleTargets,
  })
  const {compactMode} = useIssuesDashboardSurfaceLayoutState()
  const {currentPage, methods, isLoading} = useIssuesDashboardState({results: issues, pageInfo, error})
  const {fetchedQuery} = useIssuesDashboardSurfaceStateContext()
  const isFetching = isLoading || !!isPending

  // Render-null alive subscribers that invalidate the dashboard queries on real
  // change signals.
  const liveUpdates = useIssuesDashboardLiveUpdates()

  // The result-count announcement describes `pageInfo.totalCount` (all matching
  // items across pages), so derive "mixed" from query intent rather than the
  // current page's rows: a mixed query can have an issue-only page while later
  // pages contain PRs. `isExplicitIssueQuery` is page-independent and matches the
  // counted set.
  const isMixedView = !isExplicitIssueQuery(fetchedQuery)

  const {ssoOrgs} = useSso()
  const orgsRequiringSso = useMemo(
    () =>
      ssoOrgsInQuery(
        fetchedQuery,
        ssoOrgs.map(org => org['login']).filter((login): login is string => login !== undefined),
      ),
    [fetchedQuery, ssoOrgs],
  )

  const {emptyListState} = useScreenReaderAnnouncements(
    'issues',
    isFetching,
    currentPage,
    fetchedQuery,
    DEFAULT_SORTING_KEY,
    pageInfo.totalCount,
    pageInfo.totalPages,
    undefined,
    isMixedView ? {singular: 'result', plural: 'results'} : undefined,
    {...defaultSortingOptions, ...issuesDashboardSortingOptions},
  )

  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  const handleFilterSelect = (type: FilterSelectType, value: string) => {
    let newQuery: string
    switch (type) {
      case 'repo':
        newQuery = addRepoFilterToQuery(fetchedQuery, value)
        break
      case 'author':
        newQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        newQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        newQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
      default: {
        const _exhaustive: never = type
        return _exhaustive
      }
    }
    methods.updateAndSubmitQuery(newQuery)
  }

  const handleIssueFieldSelect = (fieldName: string, queryValue: string) => {
    // Delegate to the shared complexity-aware query builder used by the Relay
    // dashboard (`createReplaceIssueFieldUrl`) so grouped/boolean queries like
    // `(is:issue OR field.Priority:"Low") AND label:bug` aren't corrupted by a
    // naive tokenizer replace. Falls back to `addIssueFieldToQuery` for the
    // empty-query case where `createReplaceIssueFieldQuery` returns undefined
    // (`replaceIssueFieldInQuery`'s contract), so clicking a pill from a fresh
    // dashboard state still filters. Both helpers are the ones the Relay row
    // already uses; no bespoke query logic lives here.
    const currentQuery = fetchedQuery ?? ''
    const newQuery =
      createReplaceIssueFieldQuery(currentQuery, fieldName, queryValue) ??
      addIssueFieldToQuery(currentQuery, fieldName, queryValue)
    methods.updateAndSubmitQuery(newQuery)
  }

  const filterHref = (query: string) => `${location.pathname}?${new URLSearchParams({q: query}).toString()}`

  const getIssueTypeHref = (issueTypeName: string) => filterHref(addTypeFilterToQuery(fetchedQuery, issueTypeName))
  const getIssueBlockedHref = () => filterHref(addBlockedFilterToQuery(fetchedQuery))
  const getParentIssueHref = (repoNameWithOwner: string, number: number) =>
    filterHref(addParentIssueFilterToQuery(fetchedQuery, repoNameWithOwner, number))

  return (
    <>
      <div className="tmp-my-3">
        <SingleSignOnBanner protectedOrgs={orgsRequiringSso} />
        <div {...hpcAttrs()} {...listContainerProps}>
          {error ? (
            <SharedDashboardBlankslate dashboardType="issues" variant="error" />
          ) : (
            <>
              {liveUpdates}
              <IssuesList
                issues={issues}
                pageInfo={pageInfo}
                compactMode={compactMode}
                onCompactModeChange={updateCompactModeSetting}
                onSortingItemSelect={sort => methods.updateAndSubmitQuery(addSortToQuery(fetchedQuery, sort))}
                onStateFilterSelect={state => methods.updateAndSubmitQuery(addStateToQuery(fetchedQuery, state))}
                sortingItemSelected={getSortFromQuery(fetchedQuery)}
                stateItemSelected={getStateFromQuery(fetchedQuery)}
                emptyListState={emptyListState}
                onFilterSelect={handleFilterSelect}
                onIssueFieldSelect={handleIssueFieldSelect}
                isDeferredLoading={isPending}
                isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
                showDensityToggle={isFeatureEnabled('issues_dashboard_compact_mode')}
                onIssueSelect={onIssueSelect}
                focusShortcutsSuppressed={isSidePanelOpen}
                getIssueTypeHref={getIssueTypeHref}
                getIssueBlockedHref={getIssueBlockedHref}
                getParentIssueHref={getParentIssueHref}
                issueTypeLinkComponent={RouterLink}
              />
              {pageInfo.totalPages > 1 && (
                <Pagination
                  pageCount={pageInfo.totalPages}
                  currentPage={pageInfo.currentPage}
                  onPageChange={(e, page) => {
                    e.preventDefault()

                    methods.fetchResults({q: fetchedQuery, page})
                    window.scrollTo({top: 0})
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
